import React, { memo, useEffect, useRef } from 'react';

import { Button, Tooltip } from 'antd';

import AlertIcons from '@core/app/icons/alerts/AlertIcons';
import { getBBox } from '@core/app/svgedit/utils/getBBox';
import useI18n from '@core/helpers/useI18n';
import browser from '@core/implementations/browser';

import styles from './Info.module.scss';

interface Props {
  element: SVGElement;
  isBackgroundRemoved?: boolean;
  onRetake?: () => void;
  onToggleRemoveBackground?: () => void;
}

const Info = ({ element, isBackgroundRemoved, onRetake, onToggleRemoveBackground }: Props): React.JSX.Element => {
  const { auto_fit: t } = useI18n();
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (svgRef.current) {
      let { height, width, x, y } = getBBox(element, { ignoreRotation: false });
      const padding = 0.1;

      x -= padding * width;
      y -= padding * height;
      width *= 1 + padding * 2;
      height *= 1 + padding * 2;
      svgRef.current.setAttribute('viewBox', `${x} ${y} ${width} ${height}`);
      while (svgRef.current.firstChild) {
        svgRef.current.removeChild(svgRef.current.firstChild);
      }
      svgRef.current.appendChild(element.cloneNode(true));
    }
  }, [element]);

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <button className={styles.link} onClick={() => browser.open(t.learn_more_url)} type="button">
          {t.learn_more}
          <AlertIcons.ExtLink className={styles.icon} />
        </button>
        <ol className={styles.steps}>
          <li>{t.step1}</li>
          <li>{t.step2}</li>
          <li>{t.step3}</li>
        </ol>
        <button className={styles.link} onClick={() => browser.open(t.how_to_improve_accuracy_url)} type="button">
          {t.how_to_improve_accuracy}
        </button>
        {onRetake && (
          <Tooltip title={t.retake_tooltip}>
            <Button onClick={onRetake}>{t.retake}</Button>
          </Tooltip>
        )}
        {onToggleRemoveBackground && (
          <Tooltip title={isBackgroundRemoved ? t.use_original_image_tooltip : t.retry_with_remove_bg_tooltip}>
            <Button onClick={onToggleRemoveBackground}>
              {isBackgroundRemoved ? t.use_original_image : t.retry_with_remove_bg}
            </Button>
          </Tooltip>
        )}
      </div>
      <div className={styles.artwork}>
        <div className={styles.text}>{t.selected_artwork}:</div>
        <svg className={styles.svg} ref={svgRef} />
      </div>
    </div>
  );
};

export default memo(Info);
